import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Modal,
  TextInput,
} from 'react-native';
import { COLORS } from '../constants/theme';
import { apiService } from '../services/apiService';
import authService from '../services/authService';
import { BottomNavigation } from '../components/BottomNavigation';
import { DailyUsageChart } from '../components/DailyUsageChart';
import {
  MonthlyOverviewAnalytics,
  UserDailyAnalyticsPoint,
  UserMonthlyAnalyticsRow,
} from '../types/userAnalytics';
import { formatDuration, formatMonthLabel } from '../utils/formatDuration';

interface UserAnalyticsAdminScreenProps {
  onNavigateToProducts?: () => void;
  onNavigateToHome?: () => void;
  onNavigateToEvents?: () => void;
}

const isSameOrAfterCurrentMonth = (year: number, month: number): boolean => {
  const now = new Date();
  return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
};

export const UserAnalyticsAdminScreen: React.FC<UserAnalyticsAdminScreenProps> = ({
  onNavigateToProducts,
  onNavigateToHome,
  onNavigateToEvents,
}) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [overview, setOverview] = useState<MonthlyOverviewAnalytics | null>(null);
  const [users, setUsers] = useState<UserMonthlyAnalyticsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserMonthlyAnalyticsRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailActiveDays, setDetailActiveDays] = useState<number | null>(null);
  const [detailDaily, setDetailDaily] = useState<UserDailyAnalyticsPoint[]>([]);

  const atCurrentMonth = isSameOrAfterCurrentMonth(year, month);

  const shiftMonth = (delta: number) => {
    let nextMonth = month + delta;
    let nextYear = year;
    if (nextMonth < 1) {
      nextMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }
    const today = new Date();
    if (
      nextYear > today.getFullYear() ||
      (nextYear === today.getFullYear() && nextMonth > today.getMonth() + 1)
    ) {
      return;
    }
    setYear(nextYear);
    setMonth(nextMonth);
  };

  const loadData = useCallback(async () => {
    if (!authService.canAccessAdminAnalytics()) {
      setError('Access denied');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [overviewData, usersData] = await Promise.all([
        apiService.getAnalyticsOverviewMonthly(year, month),
        apiService.getAnalyticsUsersMonthly(year, month),
      ]);
      setOverview(overviewData);
      setUsers(usersData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error loading analytics';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((user) => {
      if (user.totalDurationSeconds <= 0 && user.sessionsCount <= 0) {
        return false;
      }
      if (query && !user.email.toLowerCase().includes(query)) {
        return false;
      }
      return true;
    });
  }, [users, search]);

  const openUserDetail = async (user: UserMonthlyAnalyticsRow) => {
    setSelectedUser(user);
    setDetailActiveDays(null);
    setDetailDaily([]);
    setDetailLoading(true);
    try {
      const detail = await apiService.getAnalyticsUserMonthly(user.userId, year, month);
      setDetailActiveDays(detail.activeDays);
      setDetailDaily(detail.daily ?? []);
    } catch {
      setDetailActiveDays(null);
      setDetailDaily([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatLastSeen = (value: string | null): string => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    const day = date.getDate().toString().padStart(2, '0');
    const mon = (date.getMonth() + 1).toString().padStart(2, '0');
    const yr = date.getFullYear();
    const hh = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${mon}.${yr} ${hh}:${mm}`;
  };

  if (!authService.canAccessAdminAnalytics()) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>You do not have permission to view usage analytics.</Text>
        {onNavigateToHome && (
          <TouchableOpacity onPress={onNavigateToHome} style={styles.retryButton}>
            <Text style={styles.retryButtonText}>Back to Home</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  if (loading && !overview) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading analytics...</Text>
      </View>
    );
  }

  if (error && !overview) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity onPress={() => void loadData()} style={styles.retryButton}>
          <Text style={styles.retryButtonText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={[styles.container, Platform.OS === 'web' && styles.containerWeb]}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Analytics</Text>
        <View style={styles.monthPicker}>
          <TouchableOpacity onPress={() => shiftMonth(-1)} style={styles.monthArrow}>
            <Text style={styles.monthArrowText}>‹</Text>
          </TouchableOpacity>
          <Text style={styles.monthLabel}>{formatMonthLabel(year, month)}</Text>
          <TouchableOpacity
            onPress={() => shiftMonth(1)}
            style={[styles.monthArrow, atCurrentMonth && styles.monthArrowDisabled]}
            disabled={atCurrentMonth}
          >
            <Text style={styles.monthArrowText}>›</Text>
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {overview && (
          <View style={styles.kpiRow}>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiValue}>{overview.mau}</Text>
              <Text style={styles.kpiLabel}>Active users</Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiValue}>{overview.totalSessions}</Text>
              <Text style={styles.kpiLabel}>Sessions</Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiValue}>{formatDuration(overview.totalDurationSeconds)}</Text>
              <Text style={styles.kpiLabel}>Total time</Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiValue}>
                {formatDuration(overview.avgSessionDurationSeconds)}
              </Text>
              <Text style={styles.kpiLabel}>Avg session</Text>
            </View>
          </View>
        )}

        {overview && (
          <View style={styles.section}>
            <DailyUsageChart
              title="Active users per day"
              points={(overview.daily ?? []).map((point) => ({ date: point.date, value: point.dau }))}
              formatValue={(value) => String(value)}
            />
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Users this month</Text>
          <Text style={styles.sectionHint}>
            Time counts while the app is in the foreground. It pauses when the tab is hidden and
            stops after 30 min without activity. Logout does not remove already counted time.
          </Text>

          <TextInput
            style={styles.searchInput}
            placeholder="Search by email"
            placeholderTextColor={COLORS.textSecondary}
            value={search}
            onChangeText={setSearch}
            autoCapitalize="none"
            autoCorrect={false}
          />

          {visibleUsers.length === 0 ? (
            <Text style={styles.emptyText}>No usage recorded for this month yet.</Text>
          ) : (
            <>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, styles.colEmail]}>Email</Text>
                <Text style={[styles.tableHeaderCell, styles.colNum]}>Sessions</Text>
                <Text style={[styles.tableHeaderCell, styles.colTime]}>Time</Text>
                <Text style={[styles.tableHeaderCell, styles.colSeen]}>Last seen</Text>
              </View>
              {visibleUsers.map((user, index) => (
                <TouchableOpacity
                  key={user.userId}
                  style={[styles.tableRow, index === visibleUsers.length - 1 && styles.tableRowLast]}
                  onPress={() => void openUserDetail(user)}
                  activeOpacity={0.7}
                >
                  <View style={styles.colEmail}>
                    <Text style={styles.rowEmail} numberOfLines={1}>
                      {user.email}
                    </Text>
                    <Text style={styles.rowRole}>{user.role}</Text>
                  </View>
                  <Text style={[styles.tableCell, styles.colNum]}>{user.sessionsCount}</Text>
                  <Text style={[styles.tableCell, styles.colTime]}>
                    {formatDuration(user.totalDurationSeconds)}
                  </Text>
                  <Text style={[styles.tableCell, styles.colSeen]} numberOfLines={1}>
                    {formatLastSeen(user.lastSeenAt)}
                  </Text>
                </TouchableOpacity>
              ))}
            </>
          )}
        </View>
      </ScrollView>

      <Modal visible={selectedUser !== null} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{selectedUser?.email}</Text>
            <Text style={styles.modalSubtitle}>{formatMonthLabel(year, month)}</Text>
            {detailLoading ? (
              <ActivityIndicator color={COLORS.primary} style={styles.modalLoader} />
            ) : (
              <ScrollView style={styles.modalScroll} showsVerticalScrollIndicator={false}>
                <DailyUsageChart
                  title="Time per day"
                  points={detailDaily.map((point) => ({
                    date: point.date,
                    value: point.totalDurationSeconds,
                  }))}
                  formatValue={formatDuration}
                  height={72}
                />
                <View style={styles.modalStats}>
                  <Text style={styles.modalStatLine}>
                    Sessions: {selectedUser?.sessionsCount ?? 0}
                  </Text>
                  <Text style={styles.modalStatLine}>
                    Active days: {detailActiveDays ?? '—'}
                  </Text>
                  <Text style={styles.modalStatLine}>
                    Total time: {formatDuration(selectedUser?.totalDurationSeconds ?? 0)}
                  </Text>
                  <Text style={styles.modalStatLine}>
                    Last seen: {formatLastSeen(selectedUser?.lastSeenAt ?? null)}
                  </Text>
                </View>
              </ScrollView>
            )}
            <TouchableOpacity
              style={styles.modalClose}
              onPress={() => setSelectedUser(null)}
            >
              <Text style={styles.modalCloseText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <BottomNavigation
        activeTab="more"
        showAnalyticsTab
        onNavigateToHome={onNavigateToHome}
        onNavigateToProducts={onNavigateToProducts}
        onNavigateToEvents={onNavigateToEvents}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  containerWeb: {
    padding: 0,
    margin: 0,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: COLORS.background,
  },
  header: {
    backgroundColor: COLORS.white,
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'web' ? 20 : 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.lightGray,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: COLORS.text,
    marginBottom: 12,
  },
  monthPicker: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  monthArrow: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: COLORS.lightGray,
    alignItems: 'center',
    justifyContent: 'center',
  },
  monthArrowDisabled: {
    opacity: 0.4,
  },
  monthArrowText: {
    fontSize: 22,
    color: COLORS.text,
    fontWeight: '600',
  },
  monthLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.text,
    minWidth: 160,
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
    gap: 16,
  },
  kpiRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  kpiCard: {
    flex: 1,
    minWidth: 140,
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.lightGray,
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: '700',
    color: COLORS.primary,
    marginBottom: 4,
  },
  kpiLabel: {
    fontSize: 13,
    color: COLORS.textSecondary,
  },
  section: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.lightGray,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: COLORS.text,
    marginBottom: 4,
  },
  sectionHint: {
    fontSize: 12,
    color: COLORS.textSecondary,
    marginBottom: 12,
  },
  searchInput: {
    borderWidth: 1,
    borderColor: COLORS.lightGray,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: COLORS.text,
    marginBottom: 12,
    backgroundColor: COLORS.white,
  },
  emptyText: {
    fontSize: 14,
    color: COLORS.textSecondary,
    textAlign: 'center',
    paddingVertical: 24,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.lightGray,
    marginBottom: 4,
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.textSecondary,
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  tableCell: {
    fontSize: 14,
    color: COLORS.text,
  },
  colEmail: {
    flex: 2,
    minWidth: 0,
  },
  colNum: {
    width: 72,
    textAlign: 'right',
  },
  colTime: {
    width: 88,
    textAlign: 'right',
  },
  colSeen: {
    flex: 1,
    minWidth: 100,
    textAlign: 'right',
    fontSize: 12,
  },
  rowEmail: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.text,
  },
  rowRole: {
    fontSize: 11,
    color: COLORS.textSecondary,
    marginTop: 2,
    textTransform: 'capitalize',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: COLORS.textSecondary,
  },
  errorText: {
    fontSize: 16,
    color: COLORS.error,
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: COLORS.primary,
    borderRadius: 8,
  },
  retryButtonText: {
    color: COLORS.white,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    backgroundColor: COLORS.white,
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 480,
    maxHeight: '80%',
  },
  modalScroll: {
    maxHeight: 360,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.text,
  },
  modalSubtitle: {
    fontSize: 14,
    color: COLORS.textSecondary,
    marginTop: 4,
    marginBottom: 16,
  },
  modalLoader: {
    marginVertical: 16,
  },
  modalStats: {
    gap: 8,
  },
  modalStatLine: {
    fontSize: 15,
    color: COLORS.text,
  },
  modalClose: {
    marginTop: 20,
    backgroundColor: COLORS.primary,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalCloseText: {
    color: COLORS.white,
    fontWeight: '600',
  },
});
